/**
 * Sprint UNIFY · #21 · Lanzador único de chat público.
 *
 * Sustituye a los dos FAB previos (Asistente IA + Equipo humano) por un
 * UNICO botón flotante bottom-right. Al pulsarlo, el visitante elige con
 * quién hablar:
 *   - 🤖 Asistente IA          (respuesta inmediata, 24/7)
 *   - 👤 Equipo humano          (deja contacto, te contestan en vivo)
 *
 * La elección se persiste en localStorage para no preguntar de nuevo —
 * incluye un enlace "Cambiar" en cada chat para volver al picker.
 *
 * Implementación: el launcher renderiza el FAB + picker. Para abrir cada
 * chat dispara un evento global (`ag:open-ai-chat` / `ag:open-human-chat`)
 * que escuchan `PublicChatWidget` y `AnonymousFloatingChat` respectivamente
 * cuando reciben la prop `controlledByLauncher`.
 *
 * `PublicChatWidget` se monta aquí en el launcher (modo controlado).
 * `AnonymousFloatingChat` ya está montado por `FloatingChatWidget` (también
 * en modo controlado) — el launcher sólo le dispara el evento de apertura.
 */
import { useState } from "react";
import { ChatCircleDots, Sparkle, UserCircle, X, ArrowRight } from "@phosphor-icons/react";
import { PublicChatWidget } from "./PublicChatWidget";

const MODE_KEY = (slug) => `ag-chat-mode-${slug}`;

function readMode(slug) {
  try { return localStorage.getItem(MODE_KEY(slug)) || null; } catch { return null; }
}
function writeMode(slug, mode) {
  try { localStorage.setItem(MODE_KEY(slug), mode); } catch {}
}
function clearMode(slug) {
  try { localStorage.removeItem(MODE_KEY(slug)); } catch {}
}

function openMode(slug, mode) {
  writeMode(slug, mode);
  if (mode === "ai") window.dispatchEvent(new CustomEvent("ag:open-ai-chat"));
  if (mode === "human") window.dispatchEvent(new CustomEvent("ag:open-human-chat"));
}

export function PublicChatLauncher({ orgSlug, orgName, onSelectService }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  if (!orgSlug) return null;

  const handlePick = (mode) => {
    setPickerOpen(false);
    setTimeout(() => openMode(orgSlug, mode), 50);
  };

  const handleFabClick = () => {
    const stored = readMode(orgSlug);
    if (stored === "ai" || stored === "human") {
      // Reabrir el chat que ya estaba usando
      openMode(orgSlug, stored);
    } else {
      setPickerOpen(true);
    }
  };

  return (
    <>
      {/* PublicChatWidget (IA) montado en modo launcher: sin FAB propio,
          escucha eventos globales para abrirse y la prop onSelectService. */}
      <PublicChatWidget
        orgSlug={orgSlug}
        orgName={orgName}
        onSelectService={onSelectService}
        controlledByLauncher
      />

      {/* Único FAB unificado bottom-right */}
      {!pickerOpen && (
        <button
          onClick={handleFabClick}
          className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-white px-4 py-3 rounded-full shadow-2xl transition-all hover:scale-105"
          data-testid="public-chat-launcher-fab"
          aria-label="Abrir chat"
        >
          <ChatCircleDots size={22} weight="fill" />
          <span className="hidden sm:inline text-sm font-semibold">¿Te ayudamos?</span>
          <span className="absolute -top-1 -right-1 bg-amber-400 text-[#1A1A1A] text-[9px] font-bold rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
            <Sparkle size={8} weight="fill" /> IA
          </span>
        </button>
      )}

      {/* Picker modal */}
      {pickerOpen && (
        <div
          className="fixed bottom-4 right-4 z-50 w-[calc(100vw-32px)] sm:w-[360px] bg-white border border-gray-200 rounded-md shadow-2xl overflow-hidden"
          data-testid="public-chat-launcher-picker"
        >
          <div className="bg-[#1A1A1A] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">Chat · {orgName || "Centro"}</div>
              <div className="font-semibold text-sm mt-0.5">¿Cómo prefieres que te ayudemos?</div>
            </div>
            <button
              onClick={() => setPickerOpen(false)}
              className="p-1.5 hover:bg-white/10 rounded"
              aria-label="Cerrar"
              data-testid="public-chat-launcher-close"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          <div className="p-3 space-y-2 bg-gray-50">
            <button
              onClick={() => handlePick("ai")}
              className="w-full text-left bg-white border border-gray-200 hover:border-[var(--ag-gold)] hover:shadow-md rounded-md p-4 flex items-start gap-3 transition-all"
              data-testid="public-chat-launcher-ai"
            >
              <div className="w-10 h-10 rounded-full bg-[var(--ag-gold)] text-white grid place-items-center shrink-0">
                <Sparkle size={20} weight="fill" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-sm text-[#1A1A1A]">Asistente IA</div>
                  <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-bold">
                    Recomendado
                  </span>
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Respuesta inmediata · servicios, precios, horarios. Puede iniciar la reserva por ti.
                </div>
              </div>
              <ArrowRight size={16} className="shrink-0 text-[var(--ag-text-muted)] mt-3" />
            </button>

            <button
              onClick={() => handlePick("human")}
              className="w-full text-left bg-white border border-gray-200 hover:border-[var(--ag-gold)] hover:shadow-md rounded-md p-4 flex items-start gap-3 transition-all"
              data-testid="public-chat-launcher-human"
            >
              <div className="w-10 h-10 rounded-full bg-[#0077B6] text-white grid place-items-center shrink-0">
                <UserCircle size={22} weight="fill" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-[#1A1A1A]">Equipo humano</div>
                <div className="text-xs text-gray-600 mt-1">
                  Deja tu nombre y un email/teléfono. Te contestamos en vivo en esta ventana.
                </div>
              </div>
              <ArrowRight size={16} className="shrink-0 text-[var(--ag-text-muted)] mt-3" />
            </button>

            <p className="text-[10px] text-gray-500 text-center pt-1">
              Tu elección se recordará en este navegador.
              <button
                onClick={() => { clearMode(orgSlug); }}
                className="text-[#0077B6] underline ml-1 hover:text-[var(--ag-gold)]"
                data-testid="public-chat-launcher-reset"
              >
                Reiniciar
              </button>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
